import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import Skeleton from "../../components/states/Skeleton";
import { listCustomers, type CustomerRow, type Tier } from "../../lib/admin/customers";
import { startImpersonation } from "../../lib/impersonate";
import { humanize } from "../../lib/labels";

/**
 * MER-195: customer index for the Admin Console. Lists every tenant
 * with tier, services count, last sync, last login, and per-row
 * action buttons.
 *
 * MER-197 wired the "View as" button: clicking it calls the
 * impersonation_start RPC, refreshes the JWT to pull in the view_as
 * claim, then navigates to the impersonated tenant's dashboard. The
 * banner above the routes shows the live impersonation state and the
 * Exit button.
 */

const TIER_LABELS: Record<Tier, string> = {
  essential: "Essential",
  growth: "Growth",
  professional: "Pro",
  enterprise: "Enterprise",
};

const TIER_TONE: Record<Tier, { bg: string; fg: string }> = {
  essential: { bg: "rgba(38,168,159,0.06)", fg: "var(--brand-deep)" },
  growth: { bg: "rgba(38,168,159,0.10)", fg: "var(--brand-deep)" },
  professional: { bg: "rgba(15,107,92,0.10)", fg: "var(--brand-deep)" },
  enterprise: { bg: "rgba(15,107,92,0.18)", fg: "var(--brand-deep)" },
};

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const SEVEN_DAYS_MS = 7 * ONE_DAY_MS;

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const delta = Date.now() - t;
  if (delta < ONE_HOUR_MS) {
    const mins = Math.max(1, Math.round(delta / (60 * 1000)));
    return `${mins}m ago`;
  }
  if (delta < ONE_DAY_MS) {
    return `${Math.round(delta / ONE_HOUR_MS)}h ago`;
  }
  return `${Math.round(delta / ONE_DAY_MS)}d ago`;
}

function syncTone(iso: string | null): string {
  if (!iso) return "var(--text-muted)";
  const delta = Date.now() - new Date(iso).getTime();
  if (delta < ONE_DAY_MS) return "var(--flag-green)";
  if (delta < SEVEN_DAYS_MS) return "var(--flag-yellow)";
  return "var(--flag-red)";
}

const cellStyle: CSSProperties = {
  padding: "12px 16px",
  fontSize: 13,
  borderBottom: "1px solid var(--border-subtle)",
  verticalAlign: "top",
};

const headerCellStyle: CSSProperties = {
  textAlign: "left",
  padding: "10px 16px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  borderBottom: "1px solid var(--border-subtle)",
  fontWeight: 700,
};

export default function CustomerIndex() {
  const [state, setState] = useState<{
    rows: CustomerRow[];
    isLoading: boolean;
    error: string | null;
  }>({ rows: [], isLoading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ rows: [], isLoading: true, error: null });
    listCustomers()
      .then((rows) => {
        if (cancelled) return;
        setState({ rows, isLoading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          rows: [],
          isLoading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main style={{ padding: "24px 32px" }}>
      <header
        style={{
          padding: "22px 28px",
          background: "linear-gradient(110deg, var(--brand-mint) 0%, #E6F3EE 50%, #FFFFFF 100%)",
          border: "1px solid var(--brand-line)",
          borderRadius: 8,
          marginBottom: 18,
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 16,
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "var(--text-2)",
              fontWeight: 700,
            }}
          >
            Admin
          </div>
          <h1
            style={{
              margin: "6px 0 0 0",
              fontSize: 26,
              fontWeight: 800,
              letterSpacing: -0.5,
              color: "var(--text)",
            }}
          >
            Customers
          </h1>
        </div>
        {/* MER-487: opens the create form. The whole /admin subtree is
            already behind StaffGate, so no extra gating here. */}
        <Link to="/admin/customers/new" style={newCustomerButtonStyle}>
          New customer
        </Link>
      </header>

      {state.isLoading ? <Skeleton label="Loading customers" height={120} /> : null}
      {state.error ? (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: 12,
            padding: 20,
            color: "var(--flag-red)",
            fontSize: 13,
          }}
        >
          Failed to load customers: {state.error}
        </div>
      ) : null}
      {!state.isLoading && !state.error && state.rows.length === 0 ? (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: 12,
            padding: 20,
            color: "var(--text-muted)",
            fontSize: 13,
          }}
        >
          No customers found.
        </div>
      ) : null}

      {!state.isLoading && !state.error && state.rows.length > 0 ? (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Customer</th>
                <th style={headerCellStyle}>Tier</th>
                <th style={headerCellStyle}>Services</th>
                <th style={headerCellStyle}>Last sync</th>
                <th style={headerCellStyle}>Last login</th>
                <th style={{ ...headerCellStyle, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.rows.map((row) => (
                <CustomerRowView key={row.tenantId} row={row} />
              ))}
            </tbody>
            {/* MER-197: row-level state lives in the child so one row's
                "View as" spinner does not block any of the others. */}
          </table>
        </div>
      ) : null}
    </main>
  );
}

function CustomerRowView(props: { row: CustomerRow }) {
  const { row } = props;
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tierLabel = row.tier ? TIER_LABELS[row.tier] : "—";
  const tierTone = row.tier ? TIER_TONE[row.tier] : null;
  const servicesTitle = row.enabledServices.length
    ? row.enabledServices.map((s) => humanize(s)).join(", ")
    : "No services configured";

  async function handleViewAs() {
    setBusy(true);
    setError(null);
    try {
      const result = await startImpersonation(row.tenantId);
      navigate(`/${result.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <tr>
      <td style={cellStyle}>
        <div style={{ fontWeight: 600, color: "var(--text)" }}>{row.name}</div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>/{row.slug}</div>
      </td>
      <td style={cellStyle}>
        {tierTone ? (
          <span
            style={{
              display: "inline-block",
              fontSize: 10,
              padding: "4px 10px",
              borderRadius: 12,
              background: tierTone.bg,
              color: tierTone.fg,
              border: "1px solid var(--brand-line)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontWeight: 700,
            }}
          >
            {tierLabel}
          </span>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>—</span>
        )}
      </td>
      <td style={cellStyle} title={servicesTitle}>
        {row.enabledServices.length}
      </td>
      <td style={{ ...cellStyle, color: syncTone(row.lastSyncAt) }}>
        {relativeTime(row.lastSyncAt)}
      </td>
      <td style={cellStyle}>
        {row.lastLoginAt ? (
          <>
            <div style={{ color: "var(--text)" }}>{row.lastLoginUserName ?? "Unknown"}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
              {relativeTime(row.lastLoginAt)}
            </div>
          </>
        ) : (
          <span
            style={{ color: "var(--text-muted)" }}
            title="No members of this tenant have signed in yet."
          >
            Never
          </span>
        )}
      </td>
      <td style={{ ...cellStyle, textAlign: "right" }}>
        <div
          style={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 4,
          }}
        >
          <div style={{ display: "inline-flex", gap: 8 }}>
            <Link to={`/admin/customers/${row.slug}`} style={detailButtonStyle}>
              Detail
            </Link>
            <button
              type="button"
              onClick={handleViewAs}
              disabled={busy}
              title={`Impersonate ${row.name} (15-minute window)`}
              style={busy ? viewAsBusyStyle : viewAsButtonStyle}
            >
              {busy ? "Switching..." : "View as"}
            </button>
          </div>
          {error ? (
            <span
              style={{ fontSize: 11, color: "var(--flag-red)", maxWidth: 240, textAlign: "right" }}
            >
              {error}
            </span>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

const newCustomerButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  fontWeight: 700,
  padding: "8px 16px",
  borderRadius: 6,
  border: "1px solid var(--brand-deep)",
  background: "var(--brand-deep)",
  color: "#FFFFFF",
  cursor: "pointer",
  textDecoration: "none",
};

const detailButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 600,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--brand-line)",
  background: "transparent",
  color: "var(--brand-deep)",
  cursor: "pointer",
  textDecoration: "none",
};

const viewAsButtonStyle: CSSProperties = {
  font: "inherit",
  fontSize: 12,
  fontWeight: 600,
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid var(--brand-deep)",
  background: "var(--brand-deep)",
  color: "#FFFFFF",
  cursor: "pointer",
};

const viewAsBusyStyle: CSSProperties = {
  ...viewAsButtonStyle,
  opacity: 0.7,
  cursor: "wait",
};
